import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDERS = [
  '{{rule_name}}',
  '{{call_id}}',
  '{{confidence}}',
  '{{reason}}',
  '{{extracted.contact_name}}',
  '{{extracted.contact_email}}',
  '{{extracted.proposed_time}}',
  '{{extracted.topic}}',
  '{{extracted_json}}',
];

const DEFAULT_SNIPPET = `{
  "event": "voicefabric_match",
  "rule": "{{rule_name}}",
  "call_id": "{{call_id}}",
  "confidence": {{confidence}},
  "contact_name": "{{extracted.contact_name}}",
  "contact_email": "{{extracted.contact_email}}",
  "proposed_time": "{{extracted.proposed_time}}",
  "topic": "{{extracted.topic}}"
}`;

export const PayloadTemplateEditor = ({ token, form, updateCfg }) => {
  const template = form.action_config?.payload_template || '';
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!template.trim()) { setPreview(null); return; }
    const t = setTimeout(() => {
      setLoading(true);
      axios.post(`${API}/admin/voicefabric/preview-payload`, {
        payload_template: template,
        rule_name: form.name || 'Preview',
      }, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setPreview(r.data))
        .catch(err => {
          console.error('Preview failed:', err);
          setPreview({ error: err.response?.data?.detail || 'Preview failed' });
        })
        .finally(() => setLoading(false));
    }, 500);
    return () => clearTimeout(t);
  }, [template, form.name, token]);

  return (
    <div className="mt-3 space-y-2" data-testid="payload-template-editor">
      <div className="flex items-center gap-2">
        <label className="text-xs text-white/50">Payload template (optional)</label>
        <button
          type="button"
          onClick={() => updateCfg('payload_template', DEFAULT_SNIPPET)}
          className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/50 hover:text-teal-300"
          data-testid="insert-default-template"
        >
          Insert default
        </button>
      </div>
      <textarea
        rows={6}
        value={template}
        onChange={e => updateCfg('payload_template', e.target.value)}
        placeholder='Leave empty to use the default shape. Use {{placeholders}} — see below.'
        className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-white text-xs font-mono placeholder-white/20 focus:outline-none focus:border-teal-500/50 resize-y"
        data-testid="payload-template-textarea"
      />
      <div className="flex flex-wrap gap-1">
        {PLACEHOLDERS.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(p);
              toast.success(`Copied ${p}`);
            }}
            className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-teal-300/70 hover:text-teal-300 hover:bg-white/10 font-mono"
          >
            {p}
          </button>
        ))}
      </div>

      {template.trim() && (
        <div className="bg-white/5 border border-white/10 rounded-lg">
          <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-white/10">
            <Eye className="w-3 h-3 text-white/40" />
            <span className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">
              Live preview
            </span>
            {loading && <Loader2 className="w-3 h-3 animate-spin text-white/30 ml-auto" />}
            {preview && !loading && (
              <span className="ml-auto text-[10px] text-white/30">
                {preview.content_type || 'application/json'}
              </span>
            )}
          </div>
          <pre
            className="text-[11px] text-emerald-300/80 font-mono p-3 overflow-x-auto whitespace-pre-wrap"
            data-testid="payload-preview"
          >
            {preview?.error
              ? preview.error
              : preview
                ? (typeof preview.body === 'string' ? preview.body : JSON.stringify(preview.body, null, 2))
                : '…'}
          </pre>
        </div>
      )}
    </div>
  );
};
